import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  FaUtensils, 
  FaFolder, 
  FaSignOutAlt, 
  FaSearch, 
  FaEdit, 
  FaCopy, 
  FaTrash, 
  FaArrowUp, 
  FaArrowDown, 
  FaFolderPlus, 
  FaPlus,
  FaSave
} from "react-icons/fa";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [activeTab, setActiveTab] = useState("products");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeGroups, setActiveGroups] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingCategoryDescription, setEditingCategoryDescription] = useState("");

  const [newGroupName, setNewGroupName] = useState("");
  const [selectedGroupCategories, setSelectedGroupCategories] = useState([]);

  const [filterCategory, setFilterCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("admin_logged_in");
    if (saved === "true") {
      setIsLoggedIn(true);
      setUserRole(localStorage.getItem("admin_role"));
      setSelectedSector(localStorage.getItem("admin_selected_sector"));
      fetchInitialData();
    }
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get("https://menu.fatipetrol.com/api/products.php"),
        axios.get("https://menu.fatipetrol.com/api/categories.php")
      ]);
      
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);

      const incomingCategories = Array.isArray(catRes.data) 
        ? catRes.data 
        : (catRes.data && Array.isArray(catRes.data.categories) ? catRes.data.categories : []);

      setCategories(incomingCategories);

      const groupsMap = {};
      incomingCategories.forEach(cat => {
        if (cat.group_name) {
          if (!groupsMap[cat.group_name]) {
            groupsMap[cat.group_name] = [];
          }
          groupsMap[cat.group_name].push(cat.name);
        }
      });

      const parsedGroups = Object.keys(groupsMap).map((key, index) => ({
        id: index + 1,
        name: key,
        categories: groupsMap[key]
      }));

      setActiveGroups(parsedGroups);

      if (incomingCategories.length > 0 && !category) {
        setCategory(incomingCategories[0].name);
      }
    } catch (err) {
      console.error(err);
      setCategories([]);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post("https://admin.fatipetrol.com/api/login.php", { username, password });
      if (res.data.success) {
        setIsLoggedIn(true);
        setUserRole(res.data.role);
        localStorage.setItem("admin_logged_in", "true");
        localStorage.setItem("admin_role", res.data.role);
        if (res.data.sector !== "all") {
          setSelectedSector("menu");
          localStorage.setItem("admin_selected_sector", "menu");
          fetchInitialData();
        }
      } else {
        alert(res.data.error);
      }
    } catch (err) {
      alert("Gabim në server gjatë kyçjes");
    }
  };

  const handleAddVariant = () => {
    setVariants([...variants, { name: "", price: "" }]);
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...variants];
    updated[index][field] = value;
    setVariants(updated);
  };

  const handleRemoveVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const hasVariants = variants.length > 0;
    const finalPrice = hasVariants ? 0 : parseFloat(price);
    
    const validVariants = variants.map(v => ({
      name: v.name.trim(),
      price: parseFloat(v.price) || 0
    })).filter(v => v.name !== "");

    const payload = { 
      id: editingId, 
      title, 
      price: finalPrice, 
      category, 
      description: description ? description : null,
      variants: hasVariants ? JSON.stringify(validVariants) : null
    };

    try {
      await axios.post("https://menu.fatipetrol.com/api/products.php", payload);
      alert(editingId ? "Produkti u përditësua me sukses!" : "Produkti u shtua me sukses!");
      resetForm();
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë ruajtjes së të dhënave.");
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      await axios.post("https://menu.fatipetrol.com/api/categories.php", { 
        name: newCategoryName,
        description: newCategoryDescription ? newCategoryDescription : null
      });
      setNewCategoryName("");
      setNewCategoryDescription("");
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë shtimit të kategorisë");
    }
  };

  const handleUpdateCategory = async (id) => {
    try {
      await axios.put("https://menu.fatipetrol.com/api/categories.php", { 
        id, 
        name: editingCategoryName,
        description: editingCategoryDescription ? editingCategoryDescription : null
      });
      
      const oldCatObj = categories.find(c => c.id === id);
      if (oldCatObj) {
        const oldName = oldCatObj.name;
        setProducts(prevProducts => 
          prevProducts.map(p => p.category === oldName ? { ...p, category: editingCategoryName } : p)
        );
      }

      setEditingCategoryId(null);
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë modifikimit të kategorisë");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("A jeni i sigurt? Fshirja e kategorisë mund të ndikojë te produktet lidhura.")) return;
    try {
      await axios.delete("https://menu.fatipetrol.com/api/categories.php", { data: { id } });
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë fshirjes");
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim() || selectedGroupCategories.length === 0) {
      alert("Ju lutem shkruani emrin e grupit dhe përzgjidhni të paktën një kategori.");
      return;
    }
    try {
      await axios.post("https://menu.fatipetrol.com/api/categories.php", {
        group_name: newGroupName,
        category_names: selectedGroupCategories
      });
      setNewGroupName("");
      setSelectedGroupCategories([]);
      alert("Grupi u krijua me sukses!");
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë ruajtjes së grupit në server");
    }
  };

  const handleCheckboxChange = (catName) => {
    if (selectedGroupCategories.includes(catName)) {
      setSelectedGroupCategories(selectedGroupCategories.filter(c => c !== catName));
    } else {
      setSelectedGroupCategories([...selectedGroupCategories, catName]);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("A jeni i sigurt që dëshironi të fshini këtë grup?")) return;
    const groupToDelete = activeGroups.find(g => g.id === id);
    if (!groupToDelete) return;
    try {
      await axios.delete("https://menu.fatipetrol.com/api/categories.php", {
        data: { delete_group_name: groupToDelete.name }
      });
      alert("Grupi u fshi me sukses!");
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë fshirjes së grupit");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setPrice("");
    setDescription("");
    setVariants([]);
    if (categories.length > 0) setCategory(categories[0].name);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setTitle(p.title);
    setPrice(p.price);
    setCategory(p.category);
    setDescription(p.description || "");
    
    if (p.variants) {
      try {
        const parsed = typeof p.variants === "string" ? JSON.parse(p.variants) : p.variants;
        setVariants(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        setVariants([]);
      }
    } else {
      setVariants([]);
    }
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClone = async (p) => {
    const payload = {
      title: `${p.title} (Klon)`,
      price: parseFloat(p.price),
      category: p.category,
      description: p.description,
      variants: p.variants ? (typeof p.variants === "string" ? p.variants : JSON.stringify(p.variants)) : null
    };
    try {
      await axios.post("https://menu.fatipetrol.com/api/products.php", payload);
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë klonimit");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("A jeni i sigurt që dëshironi ta fshini këtë produkt?")) return;
    try {
      await axios.delete("https://menu.fatipetrol.com/api/products.php", { data: { id } });
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë fshirjes");
    }
  };

  const moveCategory = (index, direction) => {
    const newCats = [...categories];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newCats.length) return;
    [newCats[index], newCats[targetIndex]] = [newCats[targetIndex], newCats[index]];
    setCategories(newCats);
  };

  const saveCategoryOrder = async () => {
    try {
      await axios.post("https://menu.fatipetrol.com/api/categories.php", { categories });
      alert("Renditja e re e kategorive u ruajt me sukses!");
      fetchInitialData();
    } catch (err) {
      alert("Gabim gjatë ruajtjes së renditjes");
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = filterCategory === "all" || p.category === filterCategory;
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (!isLoggedIn) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#f8f9fa", padding: "20px" }}>
      <form onSubmit={handleLogin} style={{ backgroundColor: "#fff", padding: "40px 30px", borderRadius: "16px", width: "100%", maxWidth: "360px", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <img src="https://menu.fatipetrol.com/assets/logo.png" alt="Fati Petrol Logo" style={{ width: "200px", height: "auto" }} />
          <p style={{ color: "#666", fontSize: "12px", margin: "10px 0 0 0", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase" }}>Admin Terminal</p>
        </div>
        <input type="text" placeholder="Përdoruesi" onChange={e => setUsername(e.target.value)} style={{ width: "100%", padding: "14px", marginBottom: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "15px", backgroundColor: "#f8fafc" }} required />
        <input type="password" placeholder="Fjalëkalimi" onChange={e => setPassword(e.target.value)} style={{ width: "100%", padding: "14px", marginBottom: "24px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "15px", backgroundColor: "#f8fafc" }} required />
        <button type="submit" style={{ width: "100%", padding: "14px", backgroundColor: "#b70a00", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "15px", transition: "all 0.2s" }}>Kyçu në Sistem</button>
      </form>
    </div>
  );

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", backgroundColor: "#f8f9fa", minHeight: "100vh", display: "flex", flexDirection: isMobile ? "column" : "row" }}>
      
      <aside style={{ width: isMobile ? "100%" : "260px", backgroundColor: "#ffffff", borderRight: "1px solid #edf2f7", display: "flex", flexDirection: "column", padding: "25px 20px", boxSizing: "border-box" }}>
        <div style={{ marginBottom: "35px", textAlign: isMobile ? "center" : "left" }}>
          <img src="https://menu.fatipetrol.com/assets/logo.png" alt="Fati Petrol Logo" style={{ width: "100%", maxWidth: "180px", height: "auto" }} />
        </div>
        
        <nav style={{ display: "flex", flexDirection: isMobile ? "row" : "column", gap: "8px", width: "100%" }}>
          <button onClick={() => setActiveTab("products")} style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", borderRadius: "10px", border: "none", backgroundColor: activeTab === "products" ? "#b70a00" : "transparent", color: activeTab === "products" ? "#fff" : "#4a5568", cursor: "pointer", fontWeight: "700", fontSize: "14px", textAlign: "left" }}>
            <FaUtensils style={{ fontSize: "16px" }} /> Produktet
          </button>
          <button onClick={() => setActiveTab("categories")} style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", borderRadius: "10px", border: "none", backgroundColor: activeTab === "categories" ? "#b70a00" : "transparent", color: activeTab === "categories" ? "#fff" : "#4a5568", cursor: "pointer", fontWeight: "700", fontSize: "14px", textAlign: "left" }}>
            <FaFolder style={{ fontSize: "16px" }} /> Kategoritë
          </button>
        </nav>

        <div style={{ marginTop: isMobile ? "20px" : "auto", borderTop: "1px solid #edf2f7", paddingTop: "20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748" }}>Fati Petrol</div>
            <div style={{ fontSize: "12px", color: "#718096" }}>Restaurant Manager</div>
          </div>
          <button onClick={() => { localStorage.clear(); window.location.reload(); }} style={{ padding: "10px", backgroundColor: "#fff5f5", border: "none", borderRadius: "8px", color: "#e53e3e", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FaSignOutAlt style={{ fontSize: "16px" }} />
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, padding: isMobile ? "20px" : "40px", boxSizing: "border-box", overflowX: "hidden" }}>
        
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", gap: "15px", flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "800", color: "#1a202c" }}>
              {activeTab === "products" ? "Lista e Menu-së" : "Menaxhimi i Kategorive"}
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "#718096", fontSize: "14px" }}>
              {activeTab === "products" ? "Organizoni dhe përditësoni artikujt e restorantit." : "Organizoni dhe gruponi menunë e restorantit."}
            </p>
          </div>
          {activeTab === "products" && (
            <div style={{ display: "flex", gap: "10px", width: isMobile ? "100%" : "auto" }}>
              <div style={{ position: "relative", width: "100%", maxWidth: "240px" }}>
                <input type="text" placeholder="Kërko produkt..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ padding: "10px 16px 10px 38px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", width: "100%", backgroundColor: "#fff", boxSizing: "border-box" }} />
                <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#a0aec0", fontSize: "14px" }} />
              </div>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ padding: "10px 16px", borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: "#fff", fontSize: "14px", fontWeight: "600" }}>
                <option value="all">Të gjitha kategoritë</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          )}
        </header>

        {activeTab === "products" ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: "30px", alignItems: "start" }}>
            
            <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              
              <div style={{ display: isMobile ? "none" : "block", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #edf2f7", color: "#a0aec0", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      <th style={{ padding: "12px 15px" }}>Emri i Produktit</th>
                      <th style={{ padding: "12px 15px" }}>Kategoria</th>
                      <th style={{ padding: "12px 15px" }}>Çmimi</th>
                      <th style={{ padding: "12px 15px", textAlign: "right" }}>Veprimet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(p => {
                      let parsedVariants = [];
                      if (p.variants) {
                        try {
                          parsedVariants = typeof p.variants === "string" ? JSON.parse(p.variants) : p.variants;
                        } catch (e) {
                          parsedVariants = [];
                        }
                      }

                      return (
                        <tr key={p.id} style={{ borderBottom: "1px solid #f7fafc", fontSize: "15px", color: "#2d3748" }}>
                          <td style={{ padding: "15px" }}>
                            <div style={{ fontWeight: "700", color: "#1a202c" }}>{p.title}</div>
                            {p.description && <div style={{ fontSize: "12px", color: "#718096", marginTop: "2px" }}>{p.description}</div>}
                          </td>
                          <td style={{ padding: "15px" }}>
                            <span style={{ backgroundColor: "#fffaf0", color: "#dd6b20", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "700", border: "1px solid #fbeeec" }}>{p.category}</span>
                          </td>
                          <td style={{ padding: "15px" }}>
                            {parsedVariants && parsedVariants.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                {parsedVariants.map((v, i) => (
                                  <span key={i} style={{ fontSize: "13px", color: "#4a5568" }}>
                                    <strong>{v.name}:</strong> {parseFloat(v.price).toFixed(2)} €
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontWeight: "800", color: "#b70a00" }}>{parseFloat(p.price).toFixed(2)} €</span>
                            )}
                          </td>
                          <td style={{ padding: "15px", textAlign: "right" }}>
                            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                              <button onClick={() => startEdit(p)} style={{ backgroundColor: "#f7fafc", border: "1px solid #e2e8f0", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }}><FaEdit /></button>
                              <button onClick={() => handleClone(p)} style={{ backgroundColor: "#f7fafc", border: "1px solid #e2e8f0", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }}><FaCopy /></button>
                              <button onClick={() => handleDelete(p.id)} style={{ backgroundColor: "#fff5f5", border: "1px solid #fed7d7", padding: "8px 10px", borderRadius: "6px", color: "#e53e3e", cursor: "pointer", display: "flex", alignItems: "center" }}><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: isMobile ? "flex" : "none", flexDirection: "column", gap: "12px" }}>
                {filteredProducts.map(p => {
                  let parsedVariants = [];
                  if (p.variants) {
                    try {
                      parsedVariants = typeof p.variants === "string" ? JSON.parse(p.variants) : p.variants;
                    } catch (e) {
                      parsedVariants = [];
                    }
                  }

                  return (
                    <div key={p.id} style={{ display: "flex", flexDirection: "column", backgroundColor: "#f8fafc", borderRadius: "12px", padding: "16px", border: "1px solid #e2e8f0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div style={{ flex: 1, paddingRight: "8px" }}>
                          <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1a202c", margin: "0 0 4px 0" }}>{p.title}</h3>
                          {p.description && <p style={{ fontSize: "12px", color: "#718096", margin: 0, lineHeight: "1.4" }}>{p.description}</p>}
                        </div>
                        <div style={{ fontSize: "14px", textAlign: "right" }}>
                          {parsedVariants && parsedVariants.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              {parsedVariants.map((v, i) => (
                                <span key={i} style={{ color: "#4a5568" }}>
                                  <strong>{v.name}:</strong> {parseFloat(v.price).toFixed(2)} €
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: "15px", fontWeight: "800", color: "#b70a00", whiteSpace: "nowrap" }}>
                              {parseFloat(p.price).toFixed(2)} €
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #edf2f7" }}>
                        <span style={{ backgroundColor: "#fffaf0", color: "#dd6b20", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700", border: "1px solid #fbeeec" }}>
                          {p.category}
                        </span>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button onClick={() => startEdit(p)} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#fff", border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: "6px", color: "#4a5568", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                            <FaEdit size={12} /> Edito
                          </button>
                          <button onClick={() => handleClone(p)} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#fff", border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: "6px", color: "#4a5568", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                            <FaCopy size={12} /> Klon
                          </button>
                          <button onClick={() => handleDelete(p.id)} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#fff5f5", border: "1px solid #fed7d7", padding: "6px 10px", borderRadius: "6px", color: "#e53e3e", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>

            <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)", position: "sticky", top: "20px" }}>
              <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "800", color: "#1a202c" }}>{editingId ? "Edito Artikullin" : "Shto Item të Ri"}</h3>
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Emri i Produktit</label>
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="p.sh. Pizza Quattro Formaggi" style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "14px" }} required />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Kategoria</label>
                  <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#fff", boxSizing: "border-box", fontSize: "14px" }}>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>

                {variants.length === 0 && (
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Çmimi (€)</label>
                    <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "14px" }} required={variants.length === 0} />
                  </div>
                )}

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", margin: 0 }}>Variante të Çmimit</label>
                    <button type="button" onClick={handleAddVariant} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", color: "#1e293b", cursor: "pointer" }}>
                      <FaPlus size={10} /> Shto Variant
                    </button>
                  </div>

                  {variants.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      {variants.map((v, index) => (
                        <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <input type="text" value={v.name} onChange={e => handleVariantChange(index, "name", e.target.value)} placeholder="p.sh. Vogël ose V" style={{ flex: 2, padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }} required />
                          <input type="number" step="0.01" value={v.price} onChange={e => handleVariantChange(index, "price", e.target.value)} placeholder="0.00 €" style={{ flex: 1, padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }} required />
                          <button type="button" onClick={() => handleRemoveVariant(index)} style={{ backgroundColor: "#fee2e2", border: "none", color: "#ef4444", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><FaTrash size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Përshkrimi</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Përbërësit ose detaje të tjera..." style={{ width: "100%", height: "90px", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontFamily: "inherit", fontSize: "14px", resize: "none" }} />
                </div>
                <button type="submit" style={{ width: "100%", padding: "14px", backgroundColor: "#b70a00", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "15px" }}>
                  {editingId ? "Përditëso Artikullin" : "Ruaj Item-in"}
                </button>
                {editingId && <button type="button" onClick={resetForm} style={{ width: "100%", padding: "10px", backgroundColor: "#edf2f7", color: "#4a5568", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>Anulo</button>}
              </form>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "30px", alignItems: "start" }}>
            
            <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ margin: "0", fontSize: "18px", fontWeight: "800", color: "#1a202c" }}>Rendit Kategoritë</h3>
                <button onClick={saveCategoryOrder} style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "transparent", color: "#b70a00", border: "none", fontWeight: "700", cursor: "pointer", fontSize: "14px" }}>
                  <FaSave size={14} /> Ruaj Renditjen
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {categories.map((cat, index) => (
                  <div key={cat.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    {editingCategoryId === cat.id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%", maxWidth: "220px" }}>
                        <input type="text" value={editingCategoryName} onChange={e => setEditingCategoryName(e.target.value)} placeholder="Emri i kategorisë" style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px" }} />
                        <input type="text" value={editingCategoryDescription} onChange={e => setEditingCategoryDescription(e.target.value)} placeholder="Përshkrimi (Opsional)" style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px" }} />
                        <button type="button" onClick={() => handleUpdateCategory(cat.id)} style={{ padding: "4px 8px", backgroundColor: "#b70a00", color: "#fff", border: "none", borderRadius: "4px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>Ruaj</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: "700", color: "#2d3748", fontSize: "15px" }}>{cat.name}</span>
                        {cat.description && <span style={{ fontSize: "12px", color: "#718096" }}>{cat.description}</span>}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => moveCategory(index, "up")} disabled={index === 0} style={{ padding: "6px 10px", backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }}><FaArrowUp /></button>
                      <button onClick={() => moveCategory(index, "down")} disabled={index === categories.length - 1} style={{ padding: "6px 10px", backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }}><FaArrowDown /></button>
                      <button onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryName(cat.name); setEditingCategoryDescription(cat.description || ""); }} style={{ padding: "8px 10px", backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }}><FaEdit /></button>
                      <button onClick={() => handleDeleteCategory(cat.id)} style={{ padding: "8px 10px", backgroundColor: "#fff5f5", border: "1px solid #fed7d7", borderRadius: "6px", color: "#e53e3e", cursor: "pointer", display: "flex", alignItems: "center" }}><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "30px", position: "sticky", top: "20px" }}>
              <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
                <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "800", color: "#1a202c" }}>Shto Kategori të Re</h3>
                <form onSubmit={handleAddCategory} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Emri i Kategorisë</label>
                    <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="p.sh. Sallata" style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "14px" }} required />
                  </div>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Përshkrimi i Kategorisë (Opsional)</label>
                    <input type="text" value={newCategoryDescription} onChange={e => setNewCategoryDescription(e.target.value)} placeholder="p.sh. Produkte të freskëta" style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "14px" }} />
                  </div>
                  <button type="submit" style={{ width: "100%", padding: "14px", backgroundColor: "#b70a00", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <FaPlus /> Shto Kategorinë
                  </button>
                </form>
              </div>

              <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
                <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "800", color: "#1a202c" }}>Krijo Grup të Ri</h3>
                <form onSubmit={handleCreateGroup} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Emri i Grupit</label>
                    <input type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="p.sh. Mëngjesi" style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", boxSizing: "border-box", fontSize: "14px" }} required />
                  </div>
                  <div>
                    <label style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Zgjidh Kategoritë për t'i bërë bashkë</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "180px", overflowY: "auto", border: "1px solid #e2e8f0", padding: "12px", borderRadius: "8px", backgroundColor: "#f8fafc" }}>
                      {categories.map(cat => (
                        <label key={cat.id} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", fontWeight: "600", color: "#2d3748", cursor: "pointer" }}>
                          <input type="checkbox" checked={selectedGroupCategories.includes(cat.name)} onChange={() => handleCheckboxChange(cat.name)} style={{ width: "16px", height: "16px", accentColor: "#b70a00", cursor: "pointer" }} />
                          {cat.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <button type="submit" style={{ width: "100%", padding: "14px", backgroundColor: "#b70a00", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <FaFolderPlus style={{ fontSize: "16px" }} /> Grupo Kategoritë
                  </button>
                </form>
              </div>

              <div style={{ backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #edf2f7", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
                <h3 style={{ margin: "0 0 15px 0", fontSize: "18px", fontWeight: "800", color: "#1a202c" }}>Grupet Aktive</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {activeGroups.map(group => (
                    <div key={group.id} style={{ border: "1px solid #edf2f7", borderRadius: "12px", padding: "16px", backgroundColor: "#ffffff" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <span style={{ fontWeight: "800", color: "#b70a00", fontSize: "15px" }}>{group.name}</span>
                        <button onClick={() => handleDeleteGroup(group.id)} style={{ backgroundColor: "transparent", border: "none", color: "#e53e3e", cursor: "pointer", display: "flex", alignItems: "center" }}><FaTrash /></button>
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {group.categories.map((c, idx) => (
                          <span key={idx} style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", color: "#4a5568" }}>{c}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}

export default App;